import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { X, Upload, Search, FileText, Image as ImageIcon, File as FileIcon, Trash2, Loader2, ExternalLink, Plus, Download, CheckSquare, ClipboardList, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { SpellCheckTextarea } from './SpellCheckTextarea';
import { calculateDueDate, SOP_OPTIONS, formatDateToLocal } from '../lib/sopRules';

// Helper function to generate filename for pasted images
const generatePastedImageFilename = () => {
  const now = new Date();
  const timestamp = now.toISOString().replace(/[-:T]/g, '').split('.')[0];
  return `pasted-image-${timestamp}.png`;
};

// Helper function to extract URLs from text
const extractUrls = (text) => {
  if (!text) return [];
  // Match URLs starting with http://, https://, or www.
  const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/gi;
  const matches = text.match(urlRegex) || [];
  // Normalize URLs and remove duplicates
  return [...new Set(matches.map(url => 
    url.startsWith('www.') ? `https://${url}` : url
  ))];
};

// Component to render clickable URLs
const ClickableUrls = ({ text }) => {
  const urls = useMemo(() => extractUrls(text), [text]);
  
  if (urls.length === 0) return null;
  
  return (
    <div className="mt-2 space-y-1">
      <p className="text-xs font-medium text-neutral-500 flex items-center gap-1">
        <Link2 className="w-3 h-3" />
        Detected Links ({urls.length}):
      </p>
      <div className="flex flex-wrap gap-2">
        {urls.map((url, index) => {
          // Try to extract domain for display
          let displayText = url;
          try {
            const urlObj = new URL(url);
            displayText = urlObj.hostname + (urlObj.pathname !== '/' ? urlObj.pathname.substring(0, 30) + (urlObj.pathname.length > 30 ? '...' : '') : '');
          } catch {
            displayText = url.substring(0, 40) + (url.length > 40 ? '...' : '');
          }
          
          return (
            <a
              key={index}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded text-xs font-medium transition-colors border border-blue-200"
              title={url}
            >
              <ExternalLink className="w-3 h-3 flex-shrink-0" />
              <span className="truncate max-w-[200px]">{displayText}</span>
            </a>
          );
        })}
      </div>
    </div>
  );
};

export default function TaskModal({ isOpen, onClose, onSubmit, onDelete, api, userType, existingTask = null, presetDueDate = null, lockedTaskType = null, defaultDate = null, lockUseDate = false, currentUserId = null }) {
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  
  // Shoot Manifesto state
  const [showManifesto, setShowManifesto] = useState(false);
  const [socialMediaTasks, setSocialMediaTasks] = useState([]);
  const [selectedManifestoTasks, setSelectedManifestoTasks] = useState([]);
  const [manualManifestoEntry, setManualManifestoEntry] = useState('');
  const [shootManifesto, setShootManifesto] = useState([]);
  const [loadingManifesto, setLoadingManifesto] = useState(false);
  
  const [formData, setFormData] = useState({
    client_id: '',
    department: '',
    title: '',
    task_type: '',
    task_subtype: '',
    description: '',
    references: '',
    attachments: [],
    priority: 'Medium',
    status: 'New',
    due_date: '',
    use_date: '',
    due_date_sop: '',
    primary_assignee_id: '',
    collaborators: [],
    recurring: 'No',
    shoot_manifesto: []
  });

  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [availableDepartments, setAvailableDepartments] = useState([]);
  const [availableSubtypes, setAvailableSubtypes] = useState([]);
  const [allDepartments, setAllDepartments] = useState([]);  // All departments for ID-to-name mapping

  useEffect(() => {
    if (isOpen) {
      fetchData();
      if (!existingTask) {
        // IMPORTANT: Reset form when opening for a new task
        // This clears any data from a previously edited task
        setFormData({
          client_id: '',
          department: '',
          title: '',
          task_type: '',
          task_subtype: '',
          description: '',
          references: '',
          attachments: [],
          priority: 'Medium',
          status: 'New',
          due_date: '',
          use_date: '',
          primary_assignee_id: '',
          collaborators: [],
          recurring: 'No',
          shoot_manifesto: []
        });
        setSelectedClient(null);
        setSelectedEmployee(null);
        setClientSearch('');
        setEmployeeSearch('');
        setShowManifesto(false);
        setSocialMediaTasks([]);
        setSelectedManifestoTasks([]);
        setManualManifestoEntry('');
        setShootManifesto([]);
        setAvailableDepartments([]);
        setAvailableSubtypes([]);
        
        // Set default values for new task (dates, locked type)
        const updates = {};
        if (presetDueDate || defaultDate) {
          if (lockUseDate) {
            updates.use_date = presetDueDate || defaultDate;
          } else {
            updates.due_date = presetDueDate || defaultDate;
            updates.use_date = presetDueDate || defaultDate;
          }
        }
        if (lockedTaskType) {
          updates.task_type = lockedTaskType;
        }
        if (Object.keys(updates).length > 0) {
          setFormData(prev => ({ ...prev, ...updates }));
        }
      }
    }
  }, [isOpen, existingTask, presetDueDate, defaultDate, lockedTaskType, lockUseDate]);

  // Populate existing task data AFTER clients, employees, and departments are loaded
  useEffect(() => {
    if (isOpen && existingTask && clients.length > 0 && employees.length > 0 && allDepartments.length > 0) {
      // [DEBUG REMOVED] console.log(...);
      populateExistingTask();
    }
  }, [isOpen, existingTask, clients, employees, allDepartments]);

  // Clipboard paste handler for images
  useEffect(() => {
    if (!isOpen) return;

    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        
        // Check if the item is an image
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          
          const file = item.getAsFile();
          if (!file) continue;

          // Generate a filename for the pasted image
          const filename = generatePastedImageFilename();
          
          // Create a new File object with a proper name
          const namedFile = new File([file], filename, { type: file.type });
          
          // Create attachment object
          const newAttachment = {
            name: filename,
            size: namedFile.size,
            type: namedFile.type,
            file: namedFile,
            preview: URL.createObjectURL(namedFile)
          };

          // Add to attachments
          setFormData(prev => ({
            ...prev,
            attachments: [...(prev.attachments || []), newAttachment]
          }));

          // Show success toast
          toast.success('Image pasted from clipboard', {
            description: filename
          });
          
          break; // Only handle the first image
        }
      }
    };

    // Add paste event listener to document when modal is open
    document.addEventListener('paste', handlePaste);
    
    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, [isOpen]);

  // Debug: Log employees state changes
  useEffect(() => {
    // console.log('[TaskModal] Employees state updated:', { count: employees.length, employees: employees.map(e => ({ id: e._id, name: e.personal?.full_name })) });
  }, [employees]);

  useEffect(() => {
    if (clients.length > 0) {
      setFilteredClients(
        clients.filter(c => 
          c.name.toLowerCase().includes(clientSearch.toLowerCase())
        )
      );
    }
  }, [clientSearch, clients]);

  useEffect(() => {
    if (employees.length > 0) {
      setFilteredEmployees(
        employees.filter(e => 
          (e.personal?.full_name || e.work_email).toLowerCase().includes(employeeSearch.toLowerCase())
        )
      );
    }
  }, [employeeSearch, employees]);

  const populateExistingTask = () => {
    if (existingTask) {
      // [DEBUG REMOVED] console.log(...);
      // Convert department ID to department name if needed
      let departmentName = existingTask.department;
      if (existingTask.department && allDepartments.length > 0) {
        // Check if department is an ID (ObjectId format) and convert to name
        const dept = allDepartments.find(d => d._id === existingTask.department);
        if (dept) {
          departmentName = dept.name;
          // [DEBUG REMOVED] console.log(...);
        }
      }
      
      // Sanitize attachments - ensure it's always an array with proper format
      let sanitizedAttachments = [];
      if (existingTask.attachments) {
        if (Array.isArray(existingTask.attachments)) {
          sanitizedAttachments = existingTask.attachments.filter(att => att != null);
        } else if (typeof existingTask.attachments === 'string') {
          // If it's a single string URL, convert to array
          sanitizedAttachments = [existingTask.attachments];
        }
      }
      
      // Sanitize shoot_manifesto - ensure it's always an array
      let sanitizedManifesto = [];
      if (existingTask.shoot_manifesto) {
        if (Array.isArray(existingTask.shoot_manifesto)) {
          sanitizedManifesto = existingTask.shoot_manifesto.filter(item => item != null);
        }
      }
      
      // Sanitize collaborators - ensure it's always an array
      let sanitizedCollaborators = [];
      if (existingTask.collaborators) {
        if (Array.isArray(existingTask.collaborators)) {
          sanitizedCollaborators = existingTask.collaborators.filter(c => c != null);
        }
      }
      
      // Fields to EXCLUDE from form data (complex objects that can cause crashes)
      const excludeFields = [
        'adapt_info',      // Complex nested dict from event tasks
        'history',         // Array of dicts for status history
        'assignee_history', // Array of dicts
        '_id',             // MongoDB ID
        '__v',             // MongoDB version
        'created_at',      // Timestamps
        'updated_at',
        'created_by',
        'created_by_type',
        'created_by_employee_id',
        'assigned_by',
        'assigned_by_type',
        'event_id',        // Event reference
        'is_active',
        'can_edit',
        'can_delete',
        'client_name',     // Computed field
        'assignee_name'    // Computed field
      ];
      
      // Create a clean copy of existingTask without excluded fields
      const cleanTask = {};
      const dateFields = new Set(['due_date', 'use_date']);
      for (const [key, value] of Object.entries(existingTask)) {
        if (!excludeFields.includes(key)) {
          // Only include primitive values or arrays of primitives/strings
          if (value === null || value === undefined) {
            cleanTask[key] = '';
          } else if (Array.isArray(value)) {
            // Keep arrays but filter out nulls
            cleanTask[key] = value.filter(item => item != null);
          } else if (typeof value === 'object' && !(value instanceof Date)) {
            // Skip complex objects
            continue;
          } else if (dateFields.has(key) && typeof value === 'string') {
            // Sanitize date strings to YYYY-MM-DD for HTML5 <input type=date>
            cleanTask[key] = value.slice(0, 10);
          } else {
            cleanTask[key] = value;
          }
        }
      }
      
      // Set form data with sanitized values
      setFormData({
        ...cleanTask,
        department: departmentName,
        attachments: sanitizedAttachments,
        shoot_manifesto: sanitizedManifesto,
        collaborators: sanitizedCollaborators,
        // Ensure required fields have defaults - handle type mismatches
        client_id: existingTask.client_id || '',
        title: existingTask.title || '',
        description: existingTask.description || '',
        // References can be string or array - convert to string
        references: Array.isArray(existingTask.references) 
          ? existingTask.references.join('\n') 
          : (existingTask.references || ''),
        priority: existingTask.priority || 'Medium',
        status: existingTask.status || 'New',
        // Slice to YYYY-MM-DD (HTML5 <input type=date> rejects ISO datetime with time component)
        due_date: (existingTask.due_date || '').toString().slice(0, 10),
        use_date: (existingTask.use_date || '').toString().slice(0, 10),
        due_date_sop: existingTask.due_date_sop || '',
        task_type: existingTask.task_type || '',
        task_subtype: existingTask.task_subtype || '',
        primary_assignee_id: existingTask.primary_assignee_id || '',
        recurring: existingTask.recurring || 'No'
      });
      
      // Find and set selected client
      const client = clients.find(c => c._id === existingTask.client_id);
      // [DEBUG REMOVED] console.log(...);
      if (client) {
        setSelectedClient(client);
        setClientSearch(client.name || '');
        setAvailableDepartments(client.departments || []);
        
        // If department name is not in client's departments, add it
        if (departmentName && !client.departments?.includes(departmentName)) {
          setAvailableDepartments([...( client.departments || []), departmentName]);
        }
      }
      
      // Find selected employee
      const employee = employees.find(e => e._id === existingTask.primary_assignee_id);
      // [DEBUG REMOVED] console.log(...);
      if (employee) {
        setSelectedEmployee(employee);
        setEmployeeSearch(employee.personal?.full_name || employee.work_email || '');
      }
    }
  };

  const fetchData = async () => {
    try {
      const employeesEndpoint = userType === 'admin' ? '/api/admin/employees' : '/api/employee/team';
      const departmentsEndpoint = userType === 'admin' ? '/api/admin/departments' : '/api/departments';
      // [DEBUG REMOVED] console.log(...);
      const [clientsRes, employeesRes, typesRes, departmentsRes] = await Promise.all([
        api.get('/api/clients'),
        api.get(employeesEndpoint),
        api.get('/api/task-types'),
        api.get(departmentsEndpoint).catch(() => ({ data: [] }))  // Fetch departments for ID-to-name mapping
      ]);
      
      // [DEBUG REMOVED] console.log(...);
      // [DEBUG REMOVED] console.log(...);
      const clientsData = Array.isArray(clientsRes.data) ? clientsRes.data : [];
      const employeesData = Array.isArray(employeesRes.data) ? employeesRes.data : [];
      const typesData = Array.isArray(typesRes.data) ? typesRes.data : [];
      const departmentsData = Array.isArray(departmentsRes.data) ? departmentsRes.data : [];
      
      // [DEBUG REMOVED] console.log(...);
      // [DEBUG REMOVED] console.log(...);
      if (employeesData.length > 0) {
        // console.log('[TaskModal] First employee:', JSON.stringify(employeesData[0]));
      }
      
      setClients(clientsData);
      setFilteredClients(clientsData);
      setEmployees(employeesData);
      setFilteredEmployees(employeesData);
      setTaskTypes(typesData);
      setAllDepartments(departmentsData);  // Store all departments for ID-to-name mapping
    } catch (error) {
      console.error('[TaskModal] Error fetching data:', error);
      console.error('[TaskModal] Error response:', error.response?.data);
      toast.error('Failed to load form data');
    }
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setFormData(prev => ({
      ...prev,
      client_id: client._id,
      department: '',
      primary_assignee_id: ''  // Reset assignee when client changes
    }));
    setAvailableDepartments(client.departments || []);
    setClientSearch(client.name);
    setShowClientDropdown(false);
    setSelectedEmployee(null);  // Reset selected employee
    setEmployeeSearch('');  // Reset search
  };

  const handleDepartmentChange = (department) => {
    // [DEBUG REMOVED] console.log(...);
    // [DEBUG REMOVED] console.log(...);
    // [DEBUG REMOVED] console.log(...);
    // [DEBUG REMOVED] console.log(...);
    setFormData(prev => ({ ...prev, department }));
    
    // Auto-assign POC if available for this department
    if (selectedClient && selectedClient.pocs && selectedClient.pocs[department]) {
      const pocEmployeeId = selectedClient.pocs[department];
      // [DEBUG REMOVED] console.log(...);
      // console.log('[TaskModal] Looking for match in employees:', employees.map(e => ({ id: e._id, name: e.personal?.full_name })));
      
      // Try to find employee by _id (handle both string and different formats)
      const pocEmployee = employees.find(e => {
        const empId = e._id || e.id;
        const matches = empId === pocEmployeeId || String(empId) === String(pocEmployeeId);
        // [DEBUG REMOVED] console.log(...);
        return matches;
      });
      
      if (pocEmployee) {
        // [DEBUG REMOVED] console.log(...);
        handleEmployeeSelect(pocEmployee);
        toast.success(`Auto-assigned ${pocEmployee.personal?.full_name || pocEmployee.work_email} as POC`);
        return;
      } else {
        // [DEBUG REMOVED] console.log(...);
      }
    } else {
      // [DEBUG REMOVED] console.log(...);
    }
    
    // Reset assignee if no POC found
    setSelectedEmployee(null);
    setEmployeeSearch('');
    setFormData(prev => ({ ...prev, department, primary_assignee_id: '' }));
  };

  // Filter employees by selected department
  const getFilteredEmployeesForDepartment = () => {
    // Safety check - ensure employees is an array
    if (!employees || !Array.isArray(employees) || employees.length === 0) {
      // console.log('[TaskModal] getFilteredEmployeesForDepartment: No employees available', { employees, isArray: Array.isArray(employees), length: employees?.length });
      return [];
    }
    
    // Filter by search term
    const searchTerm = (employeeSearch || '').toLowerCase().trim();
    
    const filtered = employees.filter(emp => {
      if (!emp) return false;
      const name = emp.personal?.full_name || emp.work_email || '';
      const email = emp.work_email || '';
      const designation = emp.designation || emp.employment?.designation || '';
      
      // If no search term, include all employees
      if (!searchTerm) return true;
      
      // Check if any field matches the search term
      return name.toLowerCase().includes(searchTerm) || 
             email.toLowerCase().includes(searchTerm) ||
             designation.toLowerCase().includes(searchTerm);
    });
    
    // [DEBUG REMOVED] console.log(...);
    return filtered;
  };

  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee);
    setFormData(prev => ({
      ...prev,
      primary_assignee_id: employee._id
    }));
    setEmployeeSearch(employee.personal?.full_name || employee.work_email);
    setShowEmployeeDropdown(false);
  };

  const handleTaskTypeChange = (taskType) => {
    const selectedType = taskTypes.find(t => t.name === taskType);
    setFormData(prev => ({
      ...prev,
      task_type: taskType,
      task_subtype: ''
    }));
    setAvailableSubtypes(selectedType?.subtypes || []);
  };

  // Load subtypes when locked task type is set and taskTypes are loaded
  useEffect(() => {
    if (lockedTaskType && taskTypes.length > 0) {
      const selectedType = taskTypes.find(t => t.name === lockedTaskType);
      if (selectedType) {
        setAvailableSubtypes(selectedType.subtypes || []);
      }
    }
  }, [lockedTaskType, taskTypes]);

  // Load subtypes when editing an existing task with a task_type
  useEffect(() => {
    if (existingTask?.task_type && taskTypes.length > 0) {
      const selectedType = taskTypes.find(t => t.name === existingTask.task_type);
      if (selectedType) {
        // [DEBUG REMOVED] console.log(...);
        setAvailableSubtypes(selectedType.subtypes || []);
        
        // Also ensure the formData.task_subtype is set correctly if it exists
        if (existingTask.task_subtype && !formData.task_subtype) {
          setFormData(prev => ({
            ...prev,
            task_subtype: existingTask.task_subtype
          }));
        }
      }
    }
  }, [existingTask, taskTypes]);

  // Load existing manifesto when editing a task
  useEffect(() => {
    if (existingTask?.shoot_manifesto && Array.isArray(existingTask.shoot_manifesto)) {
      setShootManifesto(existingTask.shoot_manifesto.filter(item => item != null));
    } else {
      setShootManifesto([]);
    }
  }, [existingTask]);

  // Check if Production task with mandatory fields filled - show manifesto option
  const isProductionTask = formData.task_type === 'Production' || lockedTaskType === 'Production';
  const mandatoryFieldsFilled = formData.client_id && formData.title && formData.due_date && formData.task_subtype;
  const canShowManifesto = isProductionTask && (mandatoryFieldsFilled || existingTask);

  // Fetch Social Media tasks for manifesto
  const fetchSocialMediaTasks = async () => {
    setLoadingManifesto(true);
    try {
      const params = formData.client_id ? { client_id: formData.client_id } : {};
      // Use the correct endpoint based on user type
      const endpoint = userType === 'admin' 
        ? '/api/tasks/social-media-next-60-days' 
        : '/api/employee/tasks/social-media-next-60-days';
      const response = await api.get(endpoint, { params });
      setSocialMediaTasks(response.data || []);
    } catch (error) {
      console.error('Error fetching social media tasks:', error);
      toast.error('Failed to load social media tasks');
    } finally {
      setLoadingManifesto(false);
    }
  };

  // Auto-fetch social media tasks when manifesto section becomes available
  useEffect(() => {
    if (canShowManifesto && socialMediaTasks.length === 0 && !loadingManifesto) {
      fetchSocialMediaTasks();
      setShowManifesto(true); // Auto-expand the manifesto section
    }
  }, [canShowManifesto]);

  // Re-fetch when client changes (to filter by selected client)
  useEffect(() => {
    if (canShowManifesto && formData.client_id) {
      fetchSocialMediaTasks();
    }
  }, [formData.client_id, canShowManifesto]);

  // Toggle manifesto task selection
  const toggleManifestoTask = (task) => {
    const isSelected = selectedManifestoTasks.find(t => t._id === task._id);
    if (isSelected) {
      setSelectedManifestoTasks(prev => prev.filter(t => t._id !== task._id));
    } else {
      setSelectedManifestoTasks(prev => [...prev, task]);
    }
  };

  // Add selected tasks to manifesto
  const addSelectedToManifesto = () => {
    const newItems = selectedManifestoTasks.map(task => ({
      _id: task._id,
      title: task.title,
      type: task.task_subtype,
      description: task.description || '',
      due_date: task.due_date,
      use_date: task.use_date || task.due_date,
      references: task.references || '',
      attachments: task.attachments || [],
      client_name: task.client_name,
      status: task.status,
      notes: '',
      source: 'social_media_task'
    }));
    
    // Add only items not already in manifesto
    const existingIds = shootManifesto.map(m => m._id);
    const filteredNew = newItems.filter(item => !existingIds.includes(item._id));
    
    setShootManifesto(prev => [...prev, ...filteredNew]);
    setSelectedManifestoTasks([]);
    toast.success(`Added ${filteredNew.length} items to manifesto`);
  };

  // Add manual entry to manifesto
  const addManualEntry = () => {
    if (!manualManifestoEntry.trim()) return;
    
    const newEntry = {
      _id: `manual_${Date.now()}`,
      title: manualManifestoEntry,
      type: 'Manual Entry',
      due_date: '',
      client_name: selectedClient?.name || '',
      status: 'Pending',
      notes: '',
      source: 'manual'
    };
    
    setShootManifesto(prev => [...prev, newEntry]);
    setManualManifestoEntry('');
  };

  // Remove item from manifesto
  const removeFromManifesto = (itemId) => {
    setShootManifesto(prev => prev.filter(m => m._id !== itemId));
  };

  // Update notes for manifesto item
  const updateManifestoNotes = (itemId, notes) => {
    setShootManifesto(prev => prev.map(m => 
      m._id === itemId ? { ...m, notes } : m
    ));
  };

  // Download manifesto as CSV
  const downloadManifestoCSV = () => {
    if (shootManifesto.length === 0) {
      toast.error('No items in manifesto to download');
      return;
    }
    
    const headers = ['Item', 'Type', 'Use Date', 'Client', 'Status', 'References/URLs', 'Attachments', 'Notes'];
    const rows = shootManifesto.map(item => {
      // Extract attachment filenames
      const attachmentNames = (item.attachments || [])
        .map(att => typeof att === 'string' ? att.split('/').pop() : (att.name || att.filename || 'file'))
        .join('; ');
      
      return [
        item.title,
        item.type,
        item.use_date || item.due_date || '',
        item.client_name,
        item.status,
        item.references || '',
        attachmentNames || '',
        item.notes || ''
      ];
    });
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${(cell || '').replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `shoot_manifesto_${formData.title || 'task'}_${formatDateToLocal(new Date())}.csv`;
    link.click();
    toast.success('Manifesto downloaded');
  };

  const handleDueDateChange = (date) => {
    setFormData(prev => ({
      ...prev,
      due_date: date,
      use_date: prev.use_date || date
    }));
  };

  // Smart-sync: when user changes use_date AND task has SOP rule, auto-recalc due_date
  // (Mirrors the Calendar drag-drop behaviour so the buffer is preserved)
  const handleUseDateChange = (newUseDate) => {
    setFormData(prev => {
      const next = { ...prev, use_date: newUseDate };
      if (prev.due_date_sop && newUseDate) {
        try {
          const newDueObj = calculateDueDate(new Date(newUseDate), prev.due_date_sop);
          next.due_date = formatDateToLocal(newDueObj);
        } catch { /* keep existing due_date if calc fails */ }
      }
      return next;
    });
  };

  const handleSopChange = (sop) => {
    setFormData(prev => {
      const next = { ...prev, due_date_sop: sop };
      // If user picks an SOP and use_date is set, recompute due_date now
      if (sop && prev.use_date) {
        try {
          const newDueObj = calculateDueDate(new Date(prev.use_date), sop);
          next.due_date = formatDateToLocal(newDueObj);
        } catch { /* noop */ }
      }
      return next;
    });
  };

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const uploadFilesToS3 = async (attachments) => {
    const uploadedFiles = [];
    const totalFiles = attachments.filter(a => a.file).length;
    let uploadedCount = 0;

    for (const attachment of attachments) {
      // If it's already uploaded (has url but no file object), keep it
      if (!attachment.file && attachment.url) {
        uploadedFiles.push(attachment.url);
        continue;
      }

      // Upload new file to S3
      if (attachment.file) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', attachment.file);

        try {
          const response = await api.post('/api/files/upload-s3', formDataUpload, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          
          if (response.data?.url) {
            uploadedFiles.push(response.data.url);
          }
          uploadedCount++;
          setUploadProgress(Math.round((uploadedCount / totalFiles) * 100));
        } catch (error) {
          console.error(`Failed to upload ${attachment.name}:`, error);
          toast.error(`Failed to upload ${attachment.name}`);
        }
      }
    }

    return uploadedFiles;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.client_id || !formData.department || !formData.title || 
        !formData.task_type || !formData.due_date || !formData.primary_assignee_id) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      let attachmentUrls = [];
      
      // Upload files to S3 if there are any new files
      const newFiles = formData.attachments?.filter(a => a.file) || [];
      const existingUrls = formData.attachments?.filter(a => !a.file && (a.url || typeof a === 'string')) || [];
      
      if (newFiles.length > 0) {
        setUploading(true);
        setUploadProgress(0);
        toast.info(`Uploading ${newFiles.length} file(s)...`);
        attachmentUrls = await uploadFilesToS3(formData.attachments);
        setUploading(false);
        toast.success('Files uploaded successfully!');
      } else {
        // Keep existing URLs (for edits)
        attachmentUrls = existingUrls.map(a => a.url || a);
      }

      // Submit task with S3 URLs and manifesto
      const taskData = {
        ...formData,
        attachments: attachmentUrls,
        shoot_manifesto: isProductionTask ? shootManifesto : undefined
      };

      await onSubmit(taskData);
      onClose();
      resetForm();
    } catch (error) {
      console.error('Error submitting task:', error);
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: '',
      department: '',
      title: '',
      task_type: '',
      task_subtype: '',
      description: '',
      references: '',
      attachments: [],
      priority: 'Medium',
      status: 'New',
      due_date: '',
      use_date: '',
      primary_assignee_id: '',
      collaborators: [],
      recurring: 'No',
      shoot_manifesto: []
    });
    setSelectedClient(null);
    setSelectedEmployee(null);
    setClientSearch('');
    setEmployeeSearch('');
    // Reset manifesto state
    setShowManifesto(false);
    setSocialMediaTasks([]);
    setSelectedManifestoTasks([]);
    setManualManifestoEntry('');
    setShootManifesto([]);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#1a1a1a] rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-[#1a1a1a] border-b border-white/40 dark:border-white/10 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-2xl font-bold text-[#1a1a2e]">
            {existingTask ? 'Edit Task' : 'Create New Task'}
          </h2>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-700">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-6">
            {/* Client - Required */}
            <div>
              <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                Client <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                  <input
                    type="text"
                    value={clientSearch}
                    onChange={(e) => {
                      setClientSearch(e.target.value);
                      setShowClientDropdown(true);
                    }}
                    onFocus={() => setShowClientDropdown(true)}
                    placeholder="Search clients..."
                    className="w-full pl-10 pr-4 py-3 border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50"
                    required
                  />
                </div>
                {showClientDropdown && filteredClients.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 glass-card rounded-xl shadow-lg max-h-48 overflow-y-auto">
                    {filteredClients.map((client) => (
                      <button
                        key={client._id}
                        type="button"
                        onClick={() => handleClientSelect(client)}
                        className="w-full text-left px-4 py-2 hover:bg-white/60 text-sm"
                      >
                        {client.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Department - Required */}
            <div>
              <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                Department <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.department}
                onChange={(e) => handleDepartmentChange(e.target.value)}
                className="w-full px-4 py-3 border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50"
                required
                disabled={!selectedClient}
              >
                <option value="">Select department</option>
                {availableDepartments.map((dept) => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>

            {/* Task Title - Required */}
            <div className="col-span-2">
              <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                Task Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-3 border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50"
                placeholder="Enter task title"
                required
              />
            </div>

            {/* Task Type - Required */}
            <div>
              <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                Task Type <span className="text-red-500">*</span>
                {lockedTaskType && (
                  <span className="ml-2 text-xs text-pink-600 bg-pink-50 px-2 py-0.5 rounded-full">Locked</span>
                )}
              </label>
              <select
                value={formData.task_type}
                onChange={(e) => handleTaskTypeChange(e.target.value)}
                className={`w-full px-4 py-3 border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50 ${
                  lockedTaskType ? 'bg-neutral-100 cursor-not-allowed' : ''
                }`}
                required
                disabled={!!lockedTaskType}
              >
                <option value="">Select type</option>
                {taskTypes.map((type) => (
                  <option key={type._id} value={type.name}>{type.name}</option>
                ))}
              </select>
            </div>

            {/* Task Subtype */}
            <div>
              <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                Task Subtype
              </label>
              <select
                value={formData.task_subtype}
                onChange={(e) => setFormData({ ...formData, task_subtype: e.target.value })}
                className="w-full px-4 py-3 border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50"
                disabled={!formData.task_type}
              >
                <option value="">Select subtype</option>
                {availableSubtypes.map((subtype) => (
                  <option key={subtype} value={subtype}>{subtype}</option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div className="col-span-2">
              <SpellCheckTextarea
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={6}
                placeholder="Describe the task..."
                showGrammarCheck={true}
              />
            </div>

            {/* References/Brief/URLs */}
            <div className="col-span-2">
              <SpellCheckTextarea
                label="References / Brief / URLs"
                value={formData.references}
                onChange={(e) => setFormData({ ...formData, references: e.target.value })}
                rows={2}
                placeholder="Add reference links or briefs... (URLs will be clickable)"
                showGrammarCheck={false}
              />
              {/* Clickable URL links extracted from the text */}
              <ClickableUrls text={formData.references} />
            </div>

            {/* File Attachments */}
            <div className="col-span-2">
              <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                Attachments
              </label>
              <div className="border-2 border-dashed border-white/40 rounded-xl p-4 hover:border-pink-400 transition-colors">
                <input
                  type="file"
                  id="task-file-upload"
                  multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length > 0) {
                      const newAttachments = files.map(file => ({
                        name: file.name,
                        size: file.size,
                        type: file.type,
                        file: file,
                        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
                      }));
                      setFormData(prev => ({
                        ...prev,
                        attachments: [...(prev.attachments || []), ...newAttachments]
                      }));
                    }
                    e.target.value = ''; // Reset input
                  }}
                  className="hidden"
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip"
                />
                <label
                  htmlFor="task-file-upload"
                  className="flex flex-col items-center justify-center cursor-pointer py-4"
                >
                  <Upload className="w-8 h-8 text-neutral-400 mb-2" />
                  <span className="text-sm text-neutral-600">Click to upload files</span>
                  <span className="text-xs text-neutral-400 mt-1">Images, PDFs, Documents, Spreadsheets (Max 100MB each)</span>
                  <span className="text-xs text-pink-500 mt-2 flex items-center gap-1">
                    <ClipboardList className="w-3 h-3" />
                    Tip: Press Ctrl+V to paste images from clipboard
                  </span>
                </label>
              </div>
              
              {/* Attached Files List */}
              {formData.attachments && Array.isArray(formData.attachments) && formData.attachments.length > 0 && (
                <div className="mt-3 space-y-2">
                  {formData.attachments.filter(att => att != null).map((attachment, index) => {
                    // Handle both new file objects and existing S3 URLs
                    const isUrl = typeof attachment === 'string';
                    const name = isUrl ? (attachment.split('/').pop() || 'file') : (attachment?.name || 'file');
                    const isImage = isUrl 
                      ? name?.match(/\.(jpg|jpeg|png|gif|webp)$/i)
                      : (attachment?.type?.startsWith('image/') || name?.match(/\.(jpg|jpeg|png|gif|webp)$/i));
                    const isPdf = isUrl 
                      ? name?.endsWith('.pdf')
                      : (attachment?.type === 'application/pdf' || name?.endsWith('.pdf'));
                    const previewUrl = isUrl ? attachment : attachment?.preview;
                    
                    return (
                      <div key={index} className="flex items-center gap-3 p-2 bg-white/40 rounded-xl">
                        {isImage && previewUrl ? (
                          <img src={previewUrl} alt={name} className="w-10 h-10 rounded object-cover" />
                        ) : isPdf ? (
                          <div className="w-10 h-10 rounded bg-red-100 flex items-center justify-center">
                            <FileText className="w-5 h-5 text-red-500" />
                          </div>
                        ) : (
                          <div className="w-10 h-10 rounded bg-neutral-100 flex items-center justify-center">
                            <FileIcon className="w-5 h-5 text-neutral-500" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#1a1a2e] truncate">{name}</p>
                          <p className="text-xs text-neutral-500">
                            {attachment.size ? `${(attachment.size / 1024).toFixed(1)} KB` : isUrl ? 'Uploaded to S3' : 'Ready to upload'}
                          </p>
                        </div>
                        {isUrl && (
                          <a
                            href={attachment}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1 text-blue-500 hover:bg-blue-50 rounded"
                            title="Open file"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              attachments: prev.attachments.filter((_, i) => i !== index)
                            }));
                          }}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="w-full px-4 py-3 border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-4 py-3 border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50"
              >
                <option value="New">New</option>
                <option value="In Progress">In Progress</option>
                <option value="Review">Review</option>
                <option value="Revision">Revision</option>
                <option value="Completed">Completed</option>
              </select>
            </div>

            {/* Due Date - Required */}
            <div>
              <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                Due Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.due_date}
                onChange={(e) => handleDueDateChange(e.target.value)}
                className="w-full px-4 py-3 border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50"
                required
              />
            </div>

            {/* Use Date */}
            <div>
              <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                Use Date
                {lockUseDate && !existingTask && (
                  <span className="ml-2 text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                    Locked to selected date
                  </span>
                )}
                {lockUseDate && existingTask && (userType === 'admin' || existingTask.created_by === currentUserId) && (
                  <span className="ml-2 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                    Editable by creator
                  </span>
                )}
              </label>
              <input
                type="date"
                value={formData.use_date}
                onChange={(e) => handleUseDateChange(e.target.value)}
                className={`w-full px-4 py-3 border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50 ${
                  lockUseDate && !existingTask ? 'bg-neutral-100 cursor-not-allowed' : ''
                }`}
                disabled={lockUseDate && !existingTask}
                data-testid="use-date-input"
              />
              {formData.due_date_sop && formData.use_date && (
                <p className="mt-1 text-xs text-emerald-600">
                  Due date auto-set to {formData.due_date} via {formData.due_date_sop.replace(/_/g, ' ')} buffer
                </p>
              )}
            </div>

            {/* SOP Buffer Rule (per task) — preserved across drag-drop and edits */}
            <div className="col-span-2">
              <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                Buffer Rule (SOP)
                <span className="ml-2 text-xs text-neutral-500 font-normal">
                  Determines how due_date is auto-calculated from use_date
                </span>
              </label>
              <select
                value={formData.due_date_sop || ''}
                onChange={(e) => handleSopChange(e.target.value)}
                className="w-full px-4 py-3 border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50 bg-white"
                data-testid="due-date-sop-select"
              >
                <option value="">No buffer (manual)</option>
                {SOP_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Primary Assignee - Required */}
            <div className="col-span-2">
              <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                Primary Assignee <span className="text-red-500">*</span>
                {selectedEmployee && (
                  <span className="ml-2 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                    {selectedClient?.pocs?.[formData.department] === selectedEmployee._id ? 'Default POC' : 'Selected'}
                  </span>
                )}
              </label>
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                  <input
                    type="text"
                    value={employeeSearch}
                    onChange={(e) => {
                      setEmployeeSearch(e.target.value);
                      setShowEmployeeDropdown(true);
                      if (!e.target.value) {
                        setSelectedEmployee(null);
                        setFormData(prev => ({ ...prev, primary_assignee_id: '' }));
                      }
                    }}
                    onFocus={() => setShowEmployeeDropdown(true)}
                    placeholder={formData.department ? "Search employees..." : "Select a department first"}
                    className="w-full pl-10 pr-4 py-3 border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50"
                    required
                  />
                </div>
                {showEmployeeDropdown && (
                  <div className="absolute z-50 w-full mt-1 glass-card rounded-xl shadow-lg max-h-48 overflow-y-auto">
                    {(() => {
                      const filteredEmps = getFilteredEmployeesForDepartment();
                      
                      if (!employees || employees.length === 0) {
                        return (
                          <div className="px-4 py-3 text-sm text-center">
                            <p className="text-neutral-500 mb-2">Loading employees...</p>
                            <button 
                              type="button"
                              onClick={() => {
                                // [DEBUG REMOVED] console.log(...);
                                fetchData();
                              }}
                              className="text-xs text-pink-600 hover:underline"
                            >
                              Click to retry
                            </button>
                          </div>
                        );
                      }
                      
                      if (filteredEmps.length === 0) {
                        return (
                          <div className="px-4 py-3 text-sm text-neutral-500 text-center">
                            No employees found matching &quot;{employeeSearch}&quot;
                          </div>
                        );
                      }
                      
                      return filteredEmps.map((employee) => (
                        <button
                          key={employee._id}
                          type="button"
                          onClick={() => handleEmployeeSelect(employee)}
                          className={`w-full text-left px-4 py-2 hover:bg-pink-50 text-sm flex items-center justify-between ${
                            selectedEmployee?._id === employee._id ? 'bg-pink-50' : ''
                          }`}
                        >
                          <span>{employee.personal?.full_name || employee.work_email}</span>
                          {selectedClient?.pocs?.[formData.department] === employee._id && (
                            <span className="text-xs text-pink-600 bg-pink-100 px-2 py-0.5 rounded-full">POC</span>
                          )}
                        </button>
                      ));
                    })()}
                  </div>
                )}
              </div>
            </div>

            {/* Recurring */}
            <div className="col-span-2">
              <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">
                Recurring Task
              </label>
              <select
                value={formData.recurring}
                onChange={(e) => setFormData({ ...formData, recurring: e.target.value })}
                className="w-full px-4 py-3 border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50"
              >
                <option value="No">No</option>
                <option value="Weekly">Weekly</option>
                <option value="Monthly">Monthly</option>
              </select>
            </div>
          </div>

          {/* Shoot Manifesto Section - Only for Production tasks */}
          {canShowManifesto && (
            <div className="col-span-2 border-t border-white/40 pt-6 mt-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-[#1a1a2e]">Shoot Manifesto</h3>
                  {shootManifesto.length > 0 && (
                    <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium">
                      {shootManifesto.length} items
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {shootManifesto.length > 0 && (
                    <button
                      type="button"
                      onClick={downloadManifestoCSV}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-50 text-green-700 rounded-xl hover:bg-green-100"
                    >
                      <Download className="w-4 h-4" />
                      Download CSV
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setShowManifesto(!showManifesto);
                      if (!showManifesto) {
                        fetchSocialMediaTasks();
                      }
                    }}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-xl hover:bg-blue-100"
                  >
                    <CheckSquare className="w-4 h-4" />
                    {showManifesto ? 'Hide Tasks' : 'Add from Social Media Calendar'}
                  </button>
                </div>
              </div>

              {/* Social Media Tasks Checklist */}
              {showManifesto && (
                <div className="mb-4 bg-white/40 rounded-xl p-4 border border-white/40">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium text-neutral-700">
                      Social Media Tasks (Next 60 days) {formData.client_id ? '- Filtered by client' : '- All clients'}
                    </p>
                    {selectedManifestoTasks.length > 0 && (
                      <button
                        type="button"
                        onClick={addSelectedToManifesto}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-500 text-white rounded-xl hover:bg-blue-600"
                      >
                        <Plus className="w-4 h-4" />
                        Add Selected ({selectedManifestoTasks.length})
                      </button>
                    )}
                  </div>
                  
                  {loadingManifesto ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                    </div>
                  ) : socialMediaTasks.length === 0 ? (
                    <p className="text-sm text-neutral-500 text-center py-4">No social media tasks found for the next 60 days</p>
                  ) : (
                    <div className="max-h-48 overflow-y-auto space-y-2">
                      {/* Sort tasks by use_date (closest first) */}
                      {[...socialMediaTasks].sort((a, b) => {
                        const dateA = a.use_date || a.due_date || '9999-99-99';
                        const dateB = b.use_date || b.due_date || '9999-99-99';
                        return dateA.localeCompare(dateB);
                      }).map(task => {
                        const isSelected = selectedManifestoTasks.find(t => t._id === task._id);
                        const alreadyInManifesto = shootManifesto.find(m => m._id === task._id);
                        const displayDate = task.use_date || task.due_date;
                        const hasReferences = task.references && task.references.trim();
                        const hasAttachments = task.attachments && task.attachments.length > 0;
                        
                        return (
                          <label
                            key={task._id}
                            className={`flex items-center gap-3 p-2 rounded-xl cursor-pointer ${
                              alreadyInManifesto 
                                ? 'bg-green-50 border border-green-200' 
                                : isSelected 
                                  ? 'bg-blue-50 border border-blue-200' 
                                  : 'glass-card hover:bg-white/60'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={!!isSelected || !!alreadyInManifesto}
                              disabled={!!alreadyInManifesto}
                              onChange={() => !alreadyInManifesto && toggleManifestoTask(task)}
                              className="w-4 h-4 text-blue-600 rounded"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[#1a1a2e] truncate">{task.title}</p>
                              <p className="text-xs text-neutral-500">
                                {task.task_subtype} • {task.client_name} • Use Date: {displayDate}
                                {hasReferences && <span className="ml-1 text-blue-500">• Has URLs</span>}
                                {hasAttachments && <span className="ml-1 text-purple-500">• {task.attachments.length} files</span>}
                              </p>
                            </div>
                            {alreadyInManifesto && (
                              <span className="text-xs text-green-600 font-medium">Added</span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Manual Entry */}
              <div className="flex items-center gap-2 mb-4">
                <input
                  type="text"
                  value={manualManifestoEntry}
                  onChange={(e) => setManualManifestoEntry(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addManualEntry())}
                  placeholder="Add manual entry to manifesto..."
                  className="flex-1 px-3 py-2 border border-white/40 rounded-xl text-sm focus:ring-2 focus:ring-blue-400/30 focus:border-blue-500"
                />
                <button
                  type="button"
                  onClick={addManualEntry}
                  disabled={!manualManifestoEntry.trim()}
                  className="px-3 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              {/* Current Manifesto Items */}
              {shootManifesto.length > 0 && (
                <div className="border border-white/40 rounded-xl overflow-hidden">
                  <div className="bg-neutral-100 px-3 py-2 text-xs font-semibold text-neutral-600 uppercase tracking-wide grid grid-cols-12 gap-2">
                    <span className="col-span-3">Item</span>
                    <span className="col-span-1">Type</span>
                    <span className="col-span-2">Use Date</span>
                    <span className="col-span-2">References</span>
                    <span className="col-span-1">Files</span>
                    <span className="col-span-2">Notes</span>
                    <span className="col-span-1"></span>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {shootManifesto.map((item, index) => {
                      const urls = item.references ? extractUrls(item.references) : [];
                      const attachmentCount = item.attachments?.length || 0;
                      
                      return (
                        <div key={item._id} className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-white/30 items-center">
                          <div className="col-span-3">
                            <p className="text-sm font-medium text-[#1a1a2e] truncate">{item.title}</p>
                            <p className="text-xs text-neutral-500">{item.client_name}</p>
                          </div>
                          <div className="col-span-1">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              item.source === 'manual' 
                                ? 'bg-purple-100 text-purple-700' 
                                : 'bg-pink-100 text-pink-700'
                            }`}>
                              {item.type}
                            </span>
                          </div>
                          <div className="col-span-2 text-xs text-neutral-600">
                            {item.use_date || item.due_date || '-'}
                          </div>
                          <div className="col-span-2">
                            {urls.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {urls.slice(0, 2).map((url, idx) => (
                                  <a
                                    key={idx}
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded text-xs"
                                    title={url}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <ExternalLink className="w-3 h-3" />
                                    <span className="truncate max-w-[60px]">
                                      {(() => {
                                        try { return new URL(url).hostname.replace('www.', ''); }
                                        catch { return url.substring(0, 10); }
                                      })()}
                                    </span>
                                  </a>
                                ))}
                                {urls.length > 2 && (
                                  <span className="text-xs text-neutral-400">+{urls.length - 2}</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-neutral-400">-</span>
                            )}
                          </div>
                          <div className="col-span-1">
                            {attachmentCount > 0 ? (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-purple-50 text-purple-600 rounded text-xs">
                                <FileText className="w-3 h-3" />
                                {attachmentCount}
                              </span>
                            ) : (
                              <span className="text-xs text-neutral-400">-</span>
                            )}
                          </div>
                          <div className="col-span-2">
                            <input
                              type="text"
                              value={item.notes || ''}
                              onChange={(e) => updateManifestoNotes(item._id, e.target.value)}
                              placeholder="Notes..."
                              className="w-full px-2 py-1 text-xs border border-white/40 rounded focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                          <div className="col-span-1 flex justify-end">
                            <button
                              type="button"
                              onClick={() => removeFromManifesto(item._id)}
                              className="p-1 text-red-500 hover:bg-red-50 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Submit Buttons */}
          <div className="flex items-center justify-between pt-4 border-t border-white/40">
            <div>
              {existingTask && onDelete && (userType === 'admin' || existingTask.can_delete !== false) && (
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm('Are you sure you want to delete this task?')) {
                      onDelete(existingTask._id);
                      onClose();
                    }
                  }}
                  className="px-6 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 font-medium"
                  data-testid="delete-task-btn"
                >
                  Delete Task
                </button>
              )}
            </div>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 border border-white/40 rounded-xl text-neutral-700 hover:bg-white/60 font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={uploading}
                className={`px-6 py-3 bg-gradient-to-r from-[#FF3366] to-[#FF3366] text-white rounded-xl font-medium transition-all ${
                  uploading ? 'opacity-60 cursor-not-allowed' : 'hover:opacity-90'
                }`}
              >
                {uploading 
                  ? `Uploading... ${uploadProgress}%` 
                  : existingTask ? 'Update Task' : 'Create Task'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
